import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import type { PerkDefinition } from '@snake-royalle/shared';
import { STAKE_MULTIPLIERS, isValidStakeMultiplier, type StakeMultiplier } from '@snake-royalle/shared';
import {
  getAssociatedTokenAddressSync,
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
} from '@solana/spl-token';

const env = (import.meta as any).env;

// Trim to be defensive against hosting UIs (Vercel, Railway) that sometimes
// leave a trailing newline or surrounding whitespace on pasted env values.
const clean = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export const PAYMENT_CONFIG = {
  rpcUrl: clean(env?.VITE_SOLANA_RPC_URL) || 'https://api.devnet.solana.com',
  cluster: clean(env?.VITE_SOLANA_CLUSTER) || 'devnet',
  treasury: clean(env?.VITE_TREASURY_WALLET),
  // USDC (or any SPL) path
  mint: clean(env?.VITE_ENTRY_FEE_MINT),
  amount: BigInt(clean(env?.VITE_ENTRY_FEE_AMOUNT) || '0'),
  decimals: Number(clean(env?.VITE_ENTRY_FEE_DECIMALS) || '6'),
  symbol: clean(env?.VITE_ENTRY_FEE_SYMBOL) || 'USDC',
  // SOL native path — lamports (1 SOL = 1e9 lamports). Default 0.01 SOL.
  solAmount: BigInt(clean(env?.VITE_ENTRY_FEE_AMOUNT_SOL) || '10000000'),
};

/**
 * Validate base58 config values early, with a human-readable error naming which
 * value is wrong. Without this, the raw error surfaces as "Non-base58 character"
 * with no clue about which env var is at fault.
 */
function assertValidPublicKey(label: string, value: string): PublicKey {
  if (!value) throw new Error(`${label} is empty — check your env vars`);
  try {
    return new PublicKey(value);
  } catch {
    // Show length + first/last few chars so the user can spot whitespace / truncation
    const preview = value.length <= 12 ? value : `${value.slice(0, 6)}…${value.slice(-6)}`;
    throw new Error(
      `${label} is not a valid Solana address (got "${preview}", len=${value.length}). ` +
      `Likely whitespace in your env var.`
    );
  }
}

const SERVER_HTTP_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

export function isPaymentRequired(): boolean {
  return PAYMENT_CONFIG.amount > 0n;
}

export function formatEntryFee(): string {
  const human = Number(PAYMENT_CONFIG.amount) / 10 ** PAYMENT_CONFIG.decimals;
  return `${human} ${PAYMENT_CONFIG.symbol}`;
}

export function formatEntryFeeSOL(): string {
  const human = Number(PAYMENT_CONFIG.solAmount) / 1e9;
  return `${human} SOL`;
}

interface SolanaProvider {
  publicKey: { toString(): string; toBytes?: () => Uint8Array } | null;
  isConnected: boolean;
  signAndSendTransaction(tx: Transaction): Promise<{ signature: string }>;
  signTransaction?(tx: Transaction): Promise<Transaction>;
  connect(): Promise<{ publicKey: { toString(): string } }>;
}

function getProvider(): SolanaProvider | null {
  const win = window as any;
  if (win.phantom?.solana?.isPhantom) return win.phantom.solana;
  if (win.solflare?.isSolflare) return win.solflare;
  if (win.solana) return win.solana;
  return null;
}

/** Error thrown when Phantom's active wallet doesn't match the wallet the
 *  player is logged into the game with. Catching this lets the UI render
 *  a clear "switch wallets" message instead of a generic payment failure. */
export class WalletMismatchError extends Error {
  readonly active: string;
  readonly loggedIn: string;
  constructor(active: string, loggedIn: string) {
    super(
      `Active wallet (${active.slice(0, 4)}…${active.slice(-4)}) doesn't match your logged-in wallet (${loggedIn.slice(0, 4)}…${loggedIn.slice(-4)}). Switch accounts in Phantom and try again.`,
    );
    this.name = 'WalletMismatchError';
    this.active = active;
    this.loggedIn = loggedIn;
  }
}

/** Throw if the wallet that's about to sign isn't the one the player is
 *  logged into the game with. Prevents the "paid with the wrong wallet,
 *  rewards landed in a different player row" footgun. Returns silently
 *  when the player isn't logged in to a specific wallet (free to pick). */
function assertActiveWalletMatchesLoggedIn(active: string): void {
  const loggedIn = ((window as any).__currentUser?.walletAddress ?? '').trim();
  if (!loggedIn) return;
  if (loggedIn === active) return;
  throw new WalletMismatchError(active, loggedIn);
}

/**
 * Builds a tx that transfers the requested raw amount of the configured
 * SPL mint from the user's ATA to the treasury's ATA. Auto-creates the
 * treasury ATA (and the user's ATA, if missing) defensively.
 *
 * `amountRaw` defaults to the base PAYMENT_CONFIG.amount; callers pass
 * `PAYMENT_CONFIG.amount × stakeMultiplier` for the high-stakes flow.
 */
async function buildEntryFeeTx(
  connection: Connection,
  payer: PublicKey,
  amountRaw: bigint = PAYMENT_CONFIG.amount,
): Promise<Transaction> {
  const mint = assertValidPublicKey('VITE_ENTRY_FEE_MINT', PAYMENT_CONFIG.mint);
  const treasury = assertValidPublicKey('VITE_TREASURY_WALLET', PAYMENT_CONFIG.treasury);

  const payerAta = getAssociatedTokenAddressSync(mint, payer);
  const treasuryAta = getAssociatedTokenAddressSync(mint, treasury, true);

  const ixs: TransactionInstruction[] = [];

  // Defensive: create treasury ATA if missing (one-time cost ~0.002 SOL paid by player)
  const treasuryAccountInfo = await connection.getAccountInfo(treasuryAta);
  if (!treasuryAccountInfo) {
    ixs.push(
      createAssociatedTokenAccountInstruction(
        payer,                    // funder
        treasuryAta,
        treasury,
        mint,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      )
    );
  }

  ixs.push(
    createTransferCheckedInstruction(
      payerAta,
      mint,
      treasuryAta,
      payer,
      amountRaw,
      PAYMENT_CONFIG.decimals
    )
  );

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  const tx = new Transaction({ feePayer: payer, blockhash, lastValidBlockHeight }).add(...ixs);
  return tx;
}

export interface PayEntryFeeResult {
  signature: string;
  walletAddress: string;
}

export interface EntryTokenResult {
  entryToken: string;
  perkOptions: PerkDefinition[];
}

export type PayEntryFeeStatus =
  | 'building'
  | 'awaiting_signature'
  | 'confirming'
  | 'finalizing';

/**
 * Orchestrates: build → sign+send via wallet → confirmed → finalized.
 *
 * We wait for `finalized` (~13s) because this is real money: a `confirmed` tx
 * (~1-2s) can theoretically be rolled back by a reorg, leaving us with an entry
 * token issued for a payment that never landed. `finalized` is irreversible.
 *
 * UX: we show progressive feedback ("Confirming…" → "Finalizing…") so the wait
 * doesn't feel dead.
 */
export async function payEntryFee(
  onProgress?: (status: PayEntryFeeStatus) => void,
  multiplier: StakeMultiplier = 1,
): Promise<PayEntryFeeResult> {
  const provider = getProvider();
  if (!provider) throw new Error('No Solana wallet detected');
  if (!PAYMENT_CONFIG.treasury || !PAYMENT_CONFIG.mint) {
    throw new Error('Payment config missing (treasury/mint)');
  }

  // Connect if not already
  if (!provider.publicKey) {
    await provider.connect();
  }
  const walletAddress = provider.publicKey!.toString();
  assertActiveWalletMatchesLoggedIn(walletAddress);
  const payer = new PublicKey(walletAddress);

  onProgress?.('building');
  const connection = new Connection(PAYMENT_CONFIG.rpcUrl, 'confirmed');
  const tx = await buildEntryFeeTx(connection, payer, PAYMENT_CONFIG.amount * BigInt(multiplier));

  onProgress?.('awaiting_signature');
  const { signature } = await provider.signAndSendTransaction(tx);

  // Pre-fetch blockhash params for confirmation (handles blockhash expiry properly).
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  // Step 1: confirmed (fast feedback for the user)
  onProgress?.('confirming');
  const confirmedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed'
  );
  if (confirmedResult.value.err) {
    throw new Error(`On-chain failure: ${JSON.stringify(confirmedResult.value.err)}`);
  }

  // Step 2: finalized (irreversible — required before we trust the payment)
  onProgress?.('finalizing');
  const finalizedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'finalized'
  );
  if (finalizedResult.value.err) {
    throw new Error(`Tx reverted on finalization: ${JSON.stringify(finalizedResult.value.err)}`);
  }

  return { signature, walletAddress };
}

/** Build + send a native SOL transfer from the payer to the treasury. */
async function buildSOLTransferTx(
  connection: Connection,
  payer: PublicKey,
  lamports: bigint,
): Promise<Transaction> {
  const treasury = assertValidPublicKey('VITE_TREASURY_WALLET', PAYMENT_CONFIG.treasury);
  const ix = SystemProgram.transfer({
    fromPubkey: payer,
    toPubkey: treasury,
    lamports: Number(lamports),
  });
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  return new Transaction({ feePayer: payer, blockhash, lastValidBlockHeight }).add(ix);
}

/** Pay the entry fee with native SOL. Lamports are quoted by the server
 *  (live USDC→SOL conversion) — pass them in from `fetchPaymentQuote()`. */
export async function payEntryFeeSOL(
  lamports: bigint,
  onProgress?: (status: PayEntryFeeStatus) => void
): Promise<PayEntryFeeResult> {
  const provider = getProvider();
  if (!provider) throw new Error('No Solana wallet detected');
  if (!PAYMENT_CONFIG.treasury) throw new Error('Payment config missing (treasury)');

  if (!provider.publicKey) {
    await provider.connect();
  }
  const walletAddress = provider.publicKey!.toString();
  assertActiveWalletMatchesLoggedIn(walletAddress);
  const payer = new PublicKey(walletAddress);

  onProgress?.('building');
  const connection = new Connection(PAYMENT_CONFIG.rpcUrl, 'confirmed');
  const tx = await buildSOLTransferTx(connection, payer, lamports);

  onProgress?.('awaiting_signature');
  const { signature } = await provider.signAndSendTransaction(tx);

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  onProgress?.('confirming');
  const confirmedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed',
  );
  if (confirmedResult.value.err) {
    throw new Error(`On-chain failure: ${JSON.stringify(confirmedResult.value.err)}`);
  }

  onProgress?.('finalizing');
  const finalizedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'finalized',
  );
  if (finalizedResult.value.err) {
    throw new Error(`Tx reverted on finalization: ${JSON.stringify(finalizedResult.value.err)}`);
  }

  return { signature, walletAddress };
}

/** Live USDC + SOL amounts for the entry fee (server is the oracle).
 *  `native` is mainnet-only (server filters per cluster). When the oracle
 *  fails, `native` may carry an `error` instead of an amount — clients
 *  should hide the option in that case. `tokenProgram` is the SPL token
 *  program id (classic or Token-2022) — required for building correct
 *  ATA + transferChecked ixs. */
export interface PaymentQuote {
  /** Stake multiplier this quote was issued for. Echoed from the request
   *  (or defaulted to 1 by the server). The UI can compare against the
   *  user's current selection before signing to catch bad state. */
  multiplier: StakeMultiplier;
  usdc: { amount: bigint; decimals: number; symbol: string };
  sol:  { lamports: bigint; priceUsd: number; decimals: number; symbol: string };
  native?:
    | { amount: bigint; decimals: number; symbol: string; mint: string; tokenProgram: string }
    | { error: string };
}

/** Fetch a quote for the requested stake multiplier (defaults to 1).
 *  The server multiplies the USD target by the multiplier and quotes
 *  USDC/SOL/native amounts for that target. */
export async function fetchPaymentQuote(
  multiplier: StakeMultiplier = 1,
): Promise<PaymentQuote> {
  const url = `${SERVER_HTTP_URL}/api/payments/quote?multiplier=${multiplier}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`quote failed (${resp.status})`);
  const data = await resp.json();
  const resolved: StakeMultiplier = isValidStakeMultiplier(data.multiplier) ? data.multiplier : 1;
  const out: PaymentQuote = {
    multiplier: resolved,
    usdc: {
      amount: BigInt(data.usdc.amount),
      decimals: data.usdc.decimals,
      symbol: data.usdc.symbol,
    },
    sol: {
      lamports: BigInt(data.sol.lamports),
      priceUsd: data.sol.priceUsd,
      decimals: data.sol.decimals,
      symbol: data.sol.symbol,
    },
  };
  if (data.native) {
    if (data.native.error) {
      out.native = { error: String(data.native.error) };
    } else {
      out.native = {
        amount: BigInt(data.native.amount),
        decimals: data.native.decimals,
        symbol: data.native.symbol,
        mint: data.native.mint,
        // Default to the classic Token program if the server didn't send
        // it (older deploy). Token-2022 must be sent explicitly.
        tokenProgram: data.native.tokenProgram ?? TOKEN_PROGRAM_ID.toBase58(),
      };
    }
  }
  return out;
}

export { STAKE_MULTIPLIERS, isValidStakeMultiplier };
export type { StakeMultiplier };

/** Type guard for the native quote — narrows to the success shape. */
export function nativeQuoteIsAvailable(
  q: PaymentQuote['native']
): q is { amount: bigint; decimals: number; symbol: string; mint: string; tokenProgram: string } {
  return !!q && !('error' in q);
}

export interface PaymentConfigStatus {
  /** Whether the server is currently accepting entry-fee payments.
   *  Falsy when the arena is paused via maintenance mode. */
  acceptingEntries: boolean;
  /** Raw maintenance state string from the server (e.g. 'off',
   *  'after_current', 'immediate'). Useful for fine-grained UI. */
  maintenanceMode: string;
}

/** Fetch payment config — lightweight check (no oracle hit) used to
 *  decide whether to even open the payment modal. Falls back to
 *  "accepting" on network errors so we don't accidentally block users
 *  when the server is reachable enough to play but the config call
 *  blipped. */
export async function fetchPaymentConfigStatus(): Promise<PaymentConfigStatus> {
  try {
    const resp = await fetch(`${SERVER_HTTP_URL}/api/payments/config`);
    if (!resp.ok) return { acceptingEntries: true, maintenanceMode: 'off' };
    const data = await resp.json();
    return {
      acceptingEntries: data.acceptingEntries !== false,
      maintenanceMode: String(data.maintenanceMode ?? 'off'),
    };
  } catch {
    return { acceptingEntries: true, maintenanceMode: 'off' };
  }
}

/** Resolve + validate the SPL token program address from the server quote.
 *  Throws if it isn't one of the two known programs (classic / Token-2022). */
function resolveTokenProgram(tokenProgramAddress: string): PublicKey {
  try {
    const candidate = new PublicKey(tokenProgramAddress);
    const known = new Set([TOKEN_PROGRAM_ID.toBase58(), TOKEN_2022_PROGRAM_ID.toBase58()]);
    if (!known.has(candidate.toBase58())) {
      throw new Error(`unknown token program: ${candidate.toBase58()}`);
    }
    return candidate;
  } catch (err: any) {
    throw new Error(`Invalid native token program from server: ${err?.message ?? tokenProgramAddress}`);
  }
}

/** Read the player's SPL token balance for `mint` (raw units, both
 *  classic SPL Token and Token-2022 share the first 64+ bytes of the
 *  TokenAccount layout, so a fixed-offset slice works for both).
 *  Returns null if the ATA doesn't exist (player never received any). */
async function getNativeTokenBalance(
  connection: Connection,
  wallet: PublicKey,
  mint: PublicKey,
  tokenProgram: PublicKey,
): Promise<bigint | null> {
  const ata = getAssociatedTokenAddressSync(mint, wallet, false, tokenProgram);
  const info = await connection.getAccountInfo(ata);
  if (!info) return null;
  // TokenAccount layout: mint(32) + owner(32) + amount(u64 LE @ 64) + ...
  if (info.data.length < 72) return null;
  return info.data.readBigUInt64LE(64);
}

/** Thrown by payEntryFeeNative when the wallet doesn't have enough native
 *  token to cover the buyin. Caller (UI) should detect this class to show
 *  a tailored "buy more" message + link instead of the raw on-chain error. */
export class InsufficientNativeBalanceError extends Error {
  readonly current: bigint;
  readonly required: bigint;
  readonly mint: string;
  readonly symbol: string;
  readonly decimals: number;

  constructor(args: { current: bigint; required: bigint; mint: string; symbol: string; decimals: number }) {
    const fmt = (raw: bigint) => (Number(raw) / 10 ** args.decimals)
      .toFixed(args.decimals).replace(/0+$/, '').replace(/\.$/, '');
    super(`Not enough ${args.symbol}. You have ${fmt(args.current)}, need ${fmt(args.required)}. Get more on pump.fun.`);
    this.name = 'InsufficientNativeBalanceError';
    this.current = args.current;
    this.required = args.required;
    this.mint = args.mint;
    this.symbol = args.symbol;
    this.decimals = args.decimals;
  }

  /** Direct link to the token's pump.fun page. */
  buyUrl(): string {
    return `https://pump.fun/coin/${this.mint}`;
  }
}

/** Build a tx that transfers `amountRaw` of an arbitrary SPL mint from the
 *  user's ATA to the treasury's ATA. Used by the native-token (PMS/SNK) path —
 *  same shape as `buildEntryFeeTx` but with the mint, amount, decimals, and
 *  token program id injected (the server tells us via /api/payments/quote.native).
 *
 *  `tokenProgramAddress` MUST match the mint's owner on-chain — pump.fun
 *  uses Token-2022 (TokenzQ...), the classic SPL is Tokenkeg.... Wrong
 *  program throws IncorrectProgramId at on-chain runtime. */
async function buildNativeTokenTx(
  connection: Connection,
  payer: PublicKey,
  mintAddress: string,
  amountRaw: bigint,
  decimals: number,
  tokenProgramAddress: string,
  treasuryAddress: string = PAYMENT_CONFIG.treasury,
): Promise<Transaction> {
  const mint = assertValidPublicKey('native token mint', mintAddress);
  const treasury = assertValidPublicKey('treasury wallet', treasuryAddress);
  const tokenProgram = resolveTokenProgram(tokenProgramAddress);

  const payerAta = getAssociatedTokenAddressSync(mint, payer, false, tokenProgram);
  const treasuryAta = getAssociatedTokenAddressSync(mint, treasury, true, tokenProgram);

  const ixs: TransactionInstruction[] = [];

  const treasuryAccountInfo = await connection.getAccountInfo(treasuryAta);
  if (!treasuryAccountInfo) {
    ixs.push(
      createAssociatedTokenAccountInstruction(
        payer,
        treasuryAta,
        treasury,
        mint,
        tokenProgram,
        ASSOCIATED_TOKEN_PROGRAM_ID,
      )
    );
  }

  ixs.push(
    createTransferCheckedInstruction(
      payerAta,
      mint,
      treasuryAta,
      payer,
      amountRaw,
      decimals,
      [],
      tokenProgram,
    )
  );

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  return new Transaction({ feePayer: payer, blockhash, lastValidBlockHeight }).add(...ixs);
}

/** Pay the entry fee with the native game token (e.g. PMS). Mainnet-only.
 *  `amountRaw`, `mintAddress`, `decimals`, `tokenProgramAddress` come from
 *  `fetchPaymentQuote().native`.
 *
 *  Pre-flight balance check: reads the player's ATA balance before signing.
 *  Throws `InsufficientNativeBalanceError` if they don't have enough — so
 *  the UI can show a clear "buy more on pump.fun" message instead of the
 *  raw on-chain `InvalidAccountData` error.
 *
 *  Symbol arg is purely for the error message; it's optional and defaults
 *  to "tokens" if unknown. */
export async function payEntryFeeNative(
  amountRaw: bigint,
  mintAddress: string,
  decimals: number,
  tokenProgramAddress: string,
  onProgress?: (status: PayEntryFeeStatus) => void,
  symbol: string = 'tokens',
): Promise<PayEntryFeeResult> {
  const provider = getProvider();
  if (!provider) throw new Error('No Solana wallet detected');
  if (!PAYMENT_CONFIG.treasury) throw new Error('Payment config missing (treasury)');

  if (!provider.publicKey) await provider.connect();
  const walletAddress = provider.publicKey!.toString();
  assertActiveWalletMatchesLoggedIn(walletAddress);
  const payer = new PublicKey(walletAddress);

  onProgress?.('building');
  const connection = new Connection(PAYMENT_CONFIG.rpcUrl, 'confirmed');

  // Pre-flight balance check. RPC failure is non-fatal here — fall through
  // to the normal payment flow; the on-chain error is the final guardrail.
  const mintPk = assertValidPublicKey('native token mint', mintAddress);
  const tokenProgramPk = resolveTokenProgram(tokenProgramAddress);
  try {
    const balance = await getNativeTokenBalance(connection, payer, mintPk, tokenProgramPk);
    if (balance === null || balance < amountRaw) {
      throw new InsufficientNativeBalanceError({
        current: balance ?? 0n,
        required: amountRaw,
        mint: mintAddress,
        symbol,
        decimals,
      });
    }
  } catch (err) {
    if (err instanceof InsufficientNativeBalanceError) throw err;
    console.warn('[payEntryFeeNative] balance pre-check failed, continuing:', err);
  }

  const tx = await buildNativeTokenTx(connection, payer, mintAddress, amountRaw, decimals, tokenProgramAddress);

  onProgress?.('awaiting_signature');
  const { signature } = await provider.signAndSendTransaction(tx);

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  onProgress?.('confirming');
  const confirmedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed',
  );
  if (confirmedResult.value.err) {
    throw new Error(`On-chain failure: ${JSON.stringify(confirmedResult.value.err)}`);
  }

  onProgress?.('finalizing');
  const finalizedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'finalized',
  );
  if (finalizedResult.value.err) {
    throw new Error(`Tx reverted on finalization: ${JSON.stringify(finalizedResult.value.err)}`);
  }

  return { signature, walletAddress };
}

export interface SponsorPoolConfig {
  arenaId: string | null;
  poolToken: bigint;
  totalLbPoints: bigint;
  treasury: string;
  mint: string;
  decimals: number;
  symbol: string;
  tokenProgram: string;
  cluster: string;
  presets: Array<{ usdMicro: bigint; amountRaw: bigint }>;
}

export interface SponsorPoolVerifyResult {
  arenaId: string;
  amountRaw: bigint;
  poolToken: bigint;
  symbol: string;
  decimals: number;
}

export async function fetchSponsorPoolConfig(): Promise<SponsorPoolConfig> {
  const resp = await fetch(`${SERVER_HTTP_URL}/api/sponsor-pool/config`);
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `sponsor config failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    arenaId: data.arenaId ?? null,
    poolToken: BigInt(data.poolToken ?? '0'),
    totalLbPoints: BigInt(data.totalLbPoints ?? '0'),
    treasury: String(data.treasury ?? ''),
    mint: String(data.mint ?? ''),
    decimals: Number(data.decimals ?? 6),
    symbol: String(data.symbol ?? 'TOKEN'),
    tokenProgram: String(data.tokenProgram ?? TOKEN_PROGRAM_ID.toBase58()),
    cluster: String(data.cluster ?? 'devnet'),
    presets: Array.isArray(data.presets)
      ? data.presets.map((p: any) => ({
          usdMicro: BigInt(p.usdMicro ?? '0'),
          amountRaw: BigInt(p.amountRaw ?? '0'),
        }))
      : [],
  };
}

export async function fetchSponsorPoolQuote(usdMicro: bigint): Promise<{ usdMicro: bigint; amountRaw: bigint }> {
  const resp = await fetch(`${SERVER_HTTP_URL}/api/sponsor-pool/quote?usdMicro=${usdMicro.toString()}`);
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `sponsor quote failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    usdMicro: BigInt(data.usdMicro ?? '0'),
    amountRaw: BigInt(data.amountRaw ?? '0'),
  };
}

export async function paySponsorPoolNative(
  amountRaw: bigint,
  config: Pick<SponsorPoolConfig, 'treasury' | 'mint' | 'decimals' | 'tokenProgram' | 'symbol'>,
  onProgress?: (status: PayEntryFeeStatus) => void,
): Promise<PayEntryFeeResult> {
  const provider = getProvider();
  if (!provider) throw new Error('No Solana wallet detected');
  if (!config.treasury || !config.mint) throw new Error('Sponsor config missing treasury/mint');

  if (!provider.publicKey) await provider.connect();
  const walletAddress = provider.publicKey!.toString();
  const payer = new PublicKey(walletAddress);

  onProgress?.('building');
  const connection = new Connection(PAYMENT_CONFIG.rpcUrl, 'confirmed');

  const mintPk = assertValidPublicKey('native token mint', config.mint);
  const tokenProgramPk = resolveTokenProgram(config.tokenProgram);
  try {
    const balance = await getNativeTokenBalance(connection, payer, mintPk, tokenProgramPk);
    if (balance === null || balance < amountRaw) {
      throw new InsufficientNativeBalanceError({
        current: balance ?? 0n,
        required: amountRaw,
        mint: config.mint,
        symbol: config.symbol,
        decimals: config.decimals,
      });
    }
  } catch (err) {
    if (err instanceof InsufficientNativeBalanceError) throw err;
    console.warn('[paySponsorPoolNative] balance pre-check failed, continuing:', err);
  }

  const tx = await buildNativeTokenTx(
    connection,
    payer,
    config.mint,
    amountRaw,
    config.decimals,
    config.tokenProgram,
    config.treasury,
  );

  onProgress?.('awaiting_signature');
  const { signature } = await provider.signAndSendTransaction(tx);

  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');

  onProgress?.('confirming');
  const confirmedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'confirmed',
  );
  if (confirmedResult.value.err) {
    throw new Error(`On-chain failure: ${JSON.stringify(confirmedResult.value.err)}`);
  }

  onProgress?.('finalizing');
  const finalizedResult = await connection.confirmTransaction(
    { signature, blockhash, lastValidBlockHeight },
    'finalized',
  );
  if (finalizedResult.value.err) {
    throw new Error(`Tx reverted on finalization: ${JSON.stringify(finalizedResult.value.err)}`);
  }

  return { signature, walletAddress };
}

export async function verifySponsorPoolPayment(params: {
  signature: string;
  walletAddress: string;
  amountRaw: bigint;
}): Promise<SponsorPoolVerifyResult> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const resp = await fetch(`${SERVER_HTTP_URL}/api/sponsor-pool/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        signature: params.signature,
        walletAddress: params.walletAddress,
        amountRaw: params.amountRaw.toString(),
      }),
    });
    if (resp.ok) {
      const data = await resp.json();
      return {
        arenaId: String(data.arenaId ?? ''),
        amountRaw: BigInt(data.amountRaw ?? '0'),
        poolToken: BigInt(data.poolToken ?? '0'),
        symbol: String(data.symbol ?? ''),
        decimals: Number(data.decimals ?? 6),
      };
    }
    const data = await resp.json().catch(() => ({}));
    lastError = new Error(data.error || `sponsor verify failed (${resp.status})`);
    if (resp.status !== 425) break;
    await new Promise((resolve) => setTimeout(resolve, 2500));
  }
  throw lastError ?? new Error('sponsor verify failed');
}

// =====================================================================
// Chest reserve sponsorship — earmarked treasury balance for chest TOKEN
// drops. Same on-chain shape as the pool sponsorship, just a different
// server bucket. Reuses `paySponsorPoolNative` for the actual transfer.
// =====================================================================

export interface ChestReserveConfig {
  balanceRaw: bigint;
  treasury: string;
  mint: string;
  decimals: number;
  symbol: string;
  tokenProgram: string;
  cluster: string;
  presets: Array<{ usdMicro: bigint; amountRaw: bigint }>;
}

export interface ChestReserveVerifyResult {
  amountRaw: bigint;
  balanceRaw: bigint;
  totalDepositedRaw: bigint;
  symbol: string;
  decimals: number;
}

export async function fetchChestReserveConfig(): Promise<ChestReserveConfig> {
  const resp = await fetch(`${SERVER_HTTP_URL}/api/chest-reserve/config`);
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `chest-reserve config failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    balanceRaw: BigInt(data.balanceRaw ?? '0'),
    treasury: String(data.treasury ?? ''),
    mint: String(data.mint ?? ''),
    decimals: Number(data.decimals ?? 6),
    symbol: String(data.symbol ?? 'TOKEN'),
    tokenProgram: String(data.tokenProgram ?? TOKEN_PROGRAM_ID.toBase58()),
    cluster: String(data.cluster ?? 'devnet'),
    presets: Array.isArray(data.presets)
      ? data.presets.map((p: any) => ({
          usdMicro: BigInt(p.usdMicro ?? '0'),
          amountRaw: BigInt(p.amountRaw ?? '0'),
        }))
      : [],
  };
}

export async function verifyChestReservePayment(params: {
  signature: string;
  walletAddress: string;
  amountRaw: bigint;
}): Promise<ChestReserveVerifyResult> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const resp = await fetch(`${SERVER_HTTP_URL}/api/chest-reserve/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        signature: params.signature,
        walletAddress: params.walletAddress,
        amountRaw: params.amountRaw.toString(),
      }),
    });
    if (resp.ok) {
      const data = await resp.json();
      return {
        amountRaw: BigInt(data.amountRaw ?? '0'),
        balanceRaw: BigInt(data.balanceRaw ?? '0'),
        totalDepositedRaw: BigInt(data.totalDepositedRaw ?? '0'),
        symbol: String(data.symbol ?? ''),
        decimals: Number(data.decimals ?? 6),
      };
    }
    const data = await resp.json().catch(() => ({}));
    lastError = new Error(data.error || `chest-reserve verify failed (${resp.status})`);
    if (resp.status !== 425) break;
    await new Promise((resolve) => setTimeout(resolve, 2500));
  }
  throw lastError ?? new Error('chest-reserve verify failed');
}

/** Format helpers for display labels in the payment modal */
export function formatLamportsAsSOL(lamports: bigint): string {
  const sol = Number(lamports) / 1e9;
  // Show 4 decimals if small, otherwise 3 for compactness
  const decimals = sol < 0.01 ? 5 : sol < 1 ? 4 : 3;
  return `${sol.toFixed(decimals)} SOL`;
}

/** POST signature to server, get back a short-lived entryToken to present at room join.
 *  - `tokenType` is the legacy field (usdc | sol). Server still reads it for those paths.
 *  - `paymentAsset` is the new field (usdc | native). Mainnet-only on the server side.
 *    Mutually exclusive with `tokenType: 'sol'` — SOL is its own legacy path. */
export async function verifyAndGetEntryToken(params: {
  signature: string;
  walletAddress: string;
  perkId?: string;          // optional — pay-before-pick flow leaves this undefined
  playerName?: string;
  tokenType?: 'usdc' | 'sol';
  paymentAsset?: 'usdc' | 'native';
  /** Stake multiplier the player paid for (1/2/5/10). Defaults to 1 on
   *  the server when omitted, so old clients keep working unchanged. */
  multiplier?: StakeMultiplier;
}): Promise<EntryTokenResult> {
  const resp = await fetch(`${SERVER_HTTP_URL}/api/payments/verify-entry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `verify-entry failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    entryToken: data.entryToken,
    perkOptions: Array.isArray(data.perkOptions) ? data.perkOptions : [],
  };
}

/**
 * Dev-mode shortcut: when payment is disabled (amount=0), still hit the verify-entry
 * endpoint to get a token (server is the authority on whether payment is required).
 * We use the wallet address if present, otherwise a stub.
 */
export async function getDevEntryToken(playerName: string, perkId?: string): Promise<EntryTokenResult> {
  const provider = getProvider();
  const walletAddress = provider?.publicKey?.toString() || `guest_${Date.now()}`;
  return verifyAndGetEntryToken({
    signature: `dev_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    walletAddress,
    perkId,
    playerName,
  });
}

/**
 * True when the client is running via `vite dev` (Vite injects this).
 * Always false in production builds.
 */
export const IS_DEV_BUILD: boolean = !!env?.DEV;

/**
 * Dev-only shortcut that bypasses Phantom entirely.
 * Hits the server's `/api/payments/dev-entry` route, which only responds
 * outside of NODE_ENV=production. Returns a valid entry token in ~50ms so
 * iteration on the deal → game flow doesn't require signing every match.
 *
 * Use `?paywall=on` in the URL to opt back into the real payment flow
 * locally (handy for testing the on-chain path end-to-end).
 */
export async function getInstantDevToken(playerName: string, perkId?: string): Promise<EntryTokenResult> {
  const provider = getProvider();
  const walletAddress = provider?.publicKey?.toString() || `dev_${playerName.replace(/\W/g, '_')}`;

  const resp = await fetch(`${SERVER_HTTP_URL}/api/payments/dev-entry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletAddress, perkId, playerName }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || `dev-entry failed (${resp.status})`);
  }
  const data = await resp.json();
  return {
    entryToken: data.entryToken,
    perkOptions: Array.isArray(data.perkOptions) ? data.perkOptions : [],
  };
}

/**
 * Free-play opt-in for local dev. Default is OFF — local mirrors production
 * (real Phantom payment) so the on-chain flow stays exercised by default.
 *
 * Toggle persistently via URL params (sticks via localStorage):
 *   ?freeplay=on   → enable, save to localStorage, every reload free
 *   ?freeplay=off  → clear, back to real payment
 *   (no param)     → reads the saved value
 *
 * The server route is still gated by NODE_ENV !== 'production', so this
 * flag is a no-op in prod even if someone toggles it.
 */
const FREEPLAY_KEY = 'dev_freeplay';

export function isFreePlayEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  // URL param takes precedence + persists the choice
  const param = new URLSearchParams(window.location.search).get('freeplay');
  if (param === 'on') {
    try { localStorage.setItem(FREEPLAY_KEY, '1'); } catch { /* ignore */ }
    return true;
  }
  if (param === 'off') {
    try { localStorage.removeItem(FREEPLAY_KEY); } catch { /* ignore */ }
    return false;
  }

  try { return localStorage.getItem(FREEPLAY_KEY) === '1'; } catch { return false; }
}
